import React, { useState } from 'react';
import {
  BooleanField,
  useRecordContext,
  useUpdate,
  useNotify,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Stack,
  Typography,
  Paper,
} from '@mui/material';
import { DialogProps } from '@mui/material/Dialog';
import { StatusCell } from './StatusCell';

interface CustomBooleanFieldProps {
  source: string;
  label?: string;
  actionLabel?: string;
  onAction?: () => void;
  /** Render the compact shadcn StatusCell trigger (enriched board). */
  compact?: boolean;
}

export const CustomBooleanField = ({
  source,
  label,
  actionLabel,
  onAction,
  compact,
}: CustomBooleanFieldProps) => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const [update] = useUpdate();
  const notify = useNotify();

  if (!record) return null;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  const handleDialogClose: DialogProps['onClose'] = (_, reason) => {
    // Handle the dialog close
    setOpen(false);
  };

  const handleButtonClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
  };

  const handleToggle = async () => {
    try {
      const updatedData = {
        ...record,
        [source]: !record[source],
      };
      
      await update(
        'funerals',
        {
          id: record.id,
          data: updatedData,
          previousData: record,
        },
        {
          onSuccess: () => {
            notify('Status updated', { type: 'success' });
            setOpen(false);
          },
          onError: (error) => {
            notify('Error updating status', { type: 'error' });
          },
        }
      );
    } catch (error) {
      notify('Error updating status', { type: 'error' });
    }
  };

  const handleAction = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAction) {
      onAction();
      setOpen(false);
    }
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      {compact ? (
        <StatusCell
          variant={record[source] ? 'done' : 'na'}
          title={label}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
        />
      ) : (
        <Paper
          onClick={handleClick}
          elevation={0}
          sx={{
            display: 'inline-flex',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            backgroundColor: record[source] ? 'success.light' : 'grey.100',
            '&:hover': {
              backgroundColor: record[source] ? 'success.main' : 'grey.200',
              transform: 'translateY(-1px)',
              boxShadow: 1,
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          <BooleanField
            source={source}
            label={label}
            sx={{
              '& .MuiTypography-root': {
                color: record[source] ? 'white' : 'text.primary',
              },
            }}
          />
        </Paper>
      )}
      <div onClick={stopPropagation}>
        <Dialog 
          open={open} 
          onClose={handleDialogClose}
          onClick={stopPropagation}
          BackdropProps={{
            onClick: stopPropagation,
          }}
          PaperProps={{
            onClick: stopPropagation,
          }}
        >
          <DialogTitle>{label || source}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ minWidth: 300, my: 2 }}>
              <Typography>Current status:</Typography>
              <Switch
                checked={record[source] || false}
                onChange={handleToggle}
                color="primary"
              />
              {actionLabel && onAction && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAction}
                  sx={{ mt: 2 }}
                >
                  {actionLabel}
                </Button>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleButtonClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
}; 